import { canonicalizePuppyCadNumber } from './canonicalPuppyCadNumber'

/** GLB 文件头字节数。 */
const GLB_HEADER_BYTE_LENGTH = 12

/** GLB chunk 头字节数。 */
const GLB_CHUNK_HEADER_BYTE_LENGTH = 8

/** GLB magic：ASCII `glTF` 的 little-endian 整数。 */
const GLB_MAGIC = 0x46546c67

/** GLB 2.0 协议版本。 */
const GLB_VERSION = 2

/** GLB JSON chunk 类型。 */
const GLB_JSON_CHUNK_TYPE = 0x4e4f534a

/**
 * 递归规范化 GLB JSON chunk 中的 CAD 数值。
 *
 * @param value 当前 JSON 值。
 * @returns 保持对象键与数组顺序不变的规范 JSON 值。
 */
function canonicalizeGlbJsonValue(value: unknown): unknown {
  if (typeof value === 'number') {
    return canonicalizePuppyCadNumber(value)
  }
  if (Array.isArray(value)) {
    return value.map(canonicalizeGlbJsonValue)
  }
  if (!value || typeof value !== 'object') {
    return value
  }

  const normalized: Record<string, unknown> = {}
  Object.entries(value as Record<string, unknown>).forEach(([key, child]) => {
    normalized[key] = canonicalizeGlbJsonValue(child)
  })
  return normalized
}

/**
 * 把字节数向上对齐到 4 字节。
 *
 * @param byteLength 原始字节数。
 * @returns 4 字节对齐后的字节数。
 */
function alignGlbChunkByteLength(byteLength: number): number {
  return (byteLength + 3) & ~3
}

/**
 * 规范化 GLB JSON chunk 中的浮点末位，并保持所有二进制 chunk 原文不变。
 *
 * 设计说明：GLTFExporter 与 GLTFLoader 往返后，矩阵/四元数会出现不可见的末位漂移。
 * 这里在 GLB 输出协议边界重写 JSON chunk，避免修改运行时 Object3D，也不会触碰顶点、索引或图片字节。
 *
 * @param bytes GLTFExporter 生成的 GLB 2.0 字节。
 * @returns JSON 数值已规范化的新 GLB 字节。
 */
export function canonicalizePuppyGlbBytes(bytes: Uint8Array): Uint8Array {
  if (bytes.byteLength < GLB_HEADER_BYTE_LENGTH + GLB_CHUNK_HEADER_BYTE_LENGTH) {
    throw new Error(`PuppyCAD GLB 长度无效：${bytes.byteLength}`)
  }

  const sourceView = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  const magic = sourceView.getUint32(0, true)
  const version = sourceView.getUint32(4, true)
  const declaredByteLength = sourceView.getUint32(8, true)
  const jsonChunkByteLength = sourceView.getUint32(GLB_HEADER_BYTE_LENGTH, true)
  const jsonChunkType = sourceView.getUint32(GLB_HEADER_BYTE_LENGTH + 4, true)
  const jsonChunkStart = GLB_HEADER_BYTE_LENGTH + GLB_CHUNK_HEADER_BYTE_LENGTH
  const jsonChunkEnd = jsonChunkStart + jsonChunkByteLength

  if (magic !== GLB_MAGIC || version !== GLB_VERSION || declaredByteLength !== bytes.byteLength) {
    throw new Error('PuppyCAD GLB 文件头无效')
  }
  if (jsonChunkType !== GLB_JSON_CHUNK_TYPE || jsonChunkEnd > bytes.byteLength) {
    throw new Error('PuppyCAD GLB 缺少有效 JSON chunk')
  }

  const sourceJson = new TextDecoder().decode(bytes.subarray(jsonChunkStart, jsonChunkEnd)).trimEnd()
  const normalizedJson = canonicalizeGlbJsonValue(JSON.parse(sourceJson))
  const normalizedJsonBytes = new TextEncoder().encode(JSON.stringify(normalizedJson))
  const normalizedJsonChunkByteLength = alignGlbChunkByteLength(normalizedJsonBytes.byteLength)
  const remainingChunks = bytes.subarray(jsonChunkEnd)
  const outputByteLength = GLB_HEADER_BYTE_LENGTH
    + GLB_CHUNK_HEADER_BYTE_LENGTH
    + normalizedJsonChunkByteLength
    + remainingChunks.byteLength
  const output = new Uint8Array(outputByteLength)
  const outputView = new DataView(output.buffer)

  outputView.setUint32(0, GLB_MAGIC, true)
  outputView.setUint32(4, GLB_VERSION, true)
  outputView.setUint32(8, outputByteLength, true)
  outputView.setUint32(GLB_HEADER_BYTE_LENGTH, normalizedJsonChunkByteLength, true)
  outputView.setUint32(GLB_HEADER_BYTE_LENGTH + 4, GLB_JSON_CHUNK_TYPE, true)
  output.fill(0x20, jsonChunkStart, jsonChunkStart + normalizedJsonChunkByteLength)
  output.set(normalizedJsonBytes, jsonChunkStart)
  output.set(remainingChunks, jsonChunkStart + normalizedJsonChunkByteLength)
  return output
}
